import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Scenario } from 'src/app/care/scenarios/models';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SiteV2 } from 'src/app/shared/models';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { Optin, OptinScenario, OptinTypeEnum } from './shared';

@Injectable({
  providedIn: 'root',
})
export class OptinsService {
  readonly OPTIN_TYPE_DEFAULT = 0;
  readonly OPTIN_TYPE_RECURRING_NOTIF_MESSENGER = 1;
  readonly OPTIN_TYPE_RECURRING_NOTIF_INSTAGRAM = 2;
  readonly OPTIN_TYPE_PER_SOCIAL_MEDIA = {
    facebook: [this.OPTIN_TYPE_RECURRING_NOTIF_MESSENGER],
    instagram2: [this.OPTIN_TYPE_RECURRING_NOTIF_INSTAGRAM],
  };

  constructor(
    private httpClient: HttpClient,
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService
  ) {}

  getOptins(): Observable<Optin[]> {
    return this.httpClient
      .get<Optin[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/optins`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getUserOptins(userId: number): Observable<Optin[]> {
    return this.httpClient
      .get<Optin[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/user/${userId}/optins`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteUserOptin(userId: number, optin: Optin): Observable<any> {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/user/${userId}/optins/${optin.id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getSentOptinsWithTitle(siteId: number, scenarioId: number): Observable<Optin[]> {
    return this.httpClient
      .get<Optin[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/scenarios/${scenarioId}/recurring-notification-optins`
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  addOptin(model: { name: string; type: OptinTypeEnum; title: string }): Observable<Optin> {
    return this.httpClient
      .post<Optin>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/optins`, model)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  saveOptin(optin: Optin): Observable<Optin> {
    return this.httpClient
      .put<Optin>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/optins/${optin.id}`, optin)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteOptin(id: number): Observable<any> {
    return this.httpClient
      .delete<any>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/optins/${id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Returns some info about the scenarios that contain this optin (either via an optin action or a one time notif box)
   */
  buildOptinScenarios(scenarios: Scenario[], sitesMap: Map<number, SiteV2>): Map<number, OptinScenario[]> {
    return scenarios.reduce((result, scenario) => {
      const optinsIds = this.getOptinsIdFromScenario(scenario);
      optinsIds.forEach((optinId: number) => {
        result.set(
          optinId,
          (result.get(optinId) || []).concat({
            id: scenario.id,
            name: scenario.name,
            last_modification_date: scenario.last_modification_date,
            site: sitesMap.get(+scenario.site_id),
          })
        );
      });
      return result;
    }, new Map());
  }

  private getOptinsIdFromScenario(scenario: Scenario): Set<number> {
    const resultSet: Set<number> = new Set();

    const allBoxes: any = Object.values(scenario.graphs).map((e: any) => (e.elements ? Object.values(e.elements) : []));

    allBoxes.flat().forEach((box: any) => {
      if (box.type === 'optinAction') {
        resultSet.add(box.optin?.id);
      }
      if (box.type === 'recurringNotifBox') {
        resultSet.add(box.elementData.optinId);
      }
    });

    return resultSet;
  }
}
